import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { NodeDrag } from "~/components/NodeDrag";
import "~/styles/Sidebar.css";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import { adicionarVariavel, removerVariavel, atualizarVariavel } from "../actions/variaveisActions";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListOl, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from "~/contexts/ThemeContext";
import { useDevMode } from "~/contexts/DevModeContext";
import { sectionHasImplementedNodes, getImplementedItems, implementedNodes } from "~/utils/devModeUtils";
import { valoresItems, funcoesItems } from "~/utils/sidebarItems";

// Itens para o modo clássico (RF)
const valoresItemsClassic = [
	{ icon: "fa-solid fa-palette", title: "Cor" },
	{ icon: "fa-solid fa-image", title: "Imagem" },
	{ icon: "fa-solid fa-volume-high", title: "Áudio" },
	{ icon: "fa-solid fa-code-compare", title: "Verdadeiro|Falso" },
	{ icon: "fa-solid fa-list-ol", title: "Texto|Número" }
];

const funcoesItemsClassic = [
	{ icon: "fa-solid fa-shapes", title: "Trigonom." },
	{ icon: "fa-solid fa-hashtag", title: "Números" },
	{ icon: "fa-solid fa-hashtag", title: "Juntar" },
	{ icon: "fa-solid fa-timeline", title: "Linear" },
	{ icon: "fa-solid fa-shuffle", title: "Aleatório" }
];

const categories = [
	{
		title: "Geral",
		items: [
			{ icon: "fa fa-clock", title: "Esperar" },
			{ icon: "fa-solid fa-folder-open", title: "Atribuir" },
			{ icon: "fa-solid fa-book", title: "Ler" },
			{ icon: "fa-solid fa-desktop", title: "Mostrar" },
			{ icon: "fa-solid fa-diagram-next", title: "Fluxograma" },
			{ icon: "fa-solid fa-plane-departure", title: "Ir para" },
			{ icon: "fa-solid fa-arrows-rotate", title: "Repetir" },
			{ icon: "fa-solid fa-diamond", title: "Decidir" },
		],
	},
	{
		title: "Geometria",
		items: [
			{ icon: "fa-solid fa-diamond", title: "Fundo" },
			{ icon: "fa-solid fa-circle-dot", title: "Ponto" },
			{ icon: "fa-solid fa-diamond", title: "Seg.Reta" },
			{ icon: "fa-solid fa-diamond", title: "Reta" },
			{ icon: "fa-solid fa-square", title: "Retang" },
			{ icon: "fa-solid fa-circle", title: "Elipse" },
		],
	},
	{
		title: "Avatar",
		items: [
			{ icon: "fa-solid fa-eye", title: "Visível" },
			{ icon: "fa-solid fa-up-down-left-right", title: "Posicionar" },
			{ icon: "fa-solid fa-person-walking", title: "Andar" },
			{ icon: "fa-solid fa-arrows-turn-to-dots", title: "Virar" },
			{ icon: "fa-solid fa-pencil", title: "Lápis" },
			{ icon: "fa-solid fa-child", title: "Aparência" },
			{ icon: "fa-solid fa-arrow-pointer", title: "Clicar" },
			{ icon: "fa-solid fa-comment-dots", title: "Interagir Avatar" },
		],
	},
	{
		title: "Mídia",
		items: [
			{ icon: "fa-solid fa-music", title: "Música" },
			{ icon: "fa-solid fa-volume-high", title: "Som" },
		],
	},
];

const operatorCategories = [
	{
		title: "Matemáticos",
		items: [
			{ icon: "fa-solid fa-times", title: "Multiplicação" },
			{ icon: "fa-solid fa-divide", title: "Divisão" },
			{ icon: "fa-solid fa-plus", title: "Soma" },
			{ icon: "fa-solid fa-minus", title: "Subtração" },
			{ icon: "fa-solid fa-superscript", title: "Potenciação" },
			{ icon: "fa-solid fa-square-root-variable", title: "Radiciação" },
		],
	},
	{
		title: "Relacionais",
		items: [
			{ icon: "fa-solid fa-greater-than", title: "Maior que" },
			{ icon: "fa-solid fa-less-than", title: "Menor que" },
			{ icon: "fa-solid fa-equals", title: "Igual a" },
			{ icon: "fa-solid fa-not-equal", title: "Diferente" },
			{ icon: "fa-solid fa-less-than-equal", title: "Menor ou igual a" },
			{ icon: "fa-solid fa-greater-than-equal", title: "Maior ou igual a" },
		],
	},
	{
		title: "Lógico",
		items: [
			{ icon: "img:/images/icons/e.png", title: "E", nodeType: "Conjunção/Interseção" },
			{ icon: "img:/images/icons/ou.png", title: "OU", nodeType: "Disjunção inclusiva/União" },
			{ icon: "img:/images/icons/nao.png", title: "NÃO", nodeType: "Negação/complementação" },
			{ icon: "img:/images/icons/xou.png", title: "XOU", nodeType: "Disjunção exclusiva" },
		],
	},
];

export const Sidebar = () => {
	const variaveis = useSelector((state) => state.variaveis);
	const dispatch = useDispatch();
	const { isRfTest, sidebarCollapsed, toggleSidebar } = useTheme();
	const { devMode } = useDevMode();

	// Estados para design RF (original)
	const [activeIndex, setActiveIndex] = React.useState(0);
	const [activeOperatorIndex, setActiveOperatorIndex] = React.useState(0);
	const [draggingIndex, setDraggingIndex] = React.useState(null);

	// Estados para design RF-TEST (novo)
	const [expandedSections, setExpandedSections] = React.useState({
		comandos: true,
		operadores: false,
		valores: false,
		funcoes: false,
		variaveis: true
	});
	const [activeCommandCategory, setActiveCommandCategory] = React.useState(0);
	const [activeOperatorCategory, setActiveOperatorCategory] = React.useState(0);

	// Handlers para design RF (original)
	const handleClick = (index) => setActiveIndex(index);
	const handleOperatorClick = (index) => setActiveOperatorIndex(index);

	// Handlers para design RF-TEST (novo)
	const toggleSection = (section) => {
		setExpandedSections(prev => ({
			...prev,
			[section]: !prev[section]
		}));
	};

	const handleCommandClick = (index) => setActiveCommandCategory(index);
	const handleOperatorClickNew = (index) => setActiveOperatorCategory(index);

	// Handlers comuns
	const handleAddVariable = () => dispatch(adicionarVariavel({ valor: 0 }));
	const handleRemoveVariable = (index) => dispatch(removerVariavel(index));
	const handleChange = (index, campo, valor) => dispatch(atualizarVariavel(index, campo, valor));

	// Função para acesso rápido no design novo
	const quickAccessItems = [
		{ 
			icon: "fa-solid fa-terminal", 
			title: "Comandos", 
			category: "comandos", 
			tooltip: "Blocos de comando e fluxo"
		},
		{ 
			icon: "fa-solid fa-calculator", 
			title: "Operadores", 
			category: "operadores", 
			tooltip: "Operações matemáticas e lógicas"
		},
		{ 
			icon: "fa-solid fa-cube", 
			title: "Valores", 
			category: "valores", 
			tooltip: "Tipos de dados e constantes"
		},
		{ 
			icon: "fa-solid fa-percentage", 
			title: "Funções", 
			category: "funcoes", 
			tooltip: "Funções matemáticas avançadas"
		},
		{ 
			icon: "fa-solid fa-tags", 
			title: "Variáveis", 
			category: "variaveis", 
			tooltip: "Criar e gerenciar variáveis"
		}
	];

	const handleQuickAccess = (item) => {
		// Expandir o sidebar primeiro se estiver colapsado
		if (sidebarCollapsed) {
			toggleSidebar();
		}
		
		// Abrir apenas a seção correspondente e fechar as outras
		setExpandedSections(() => ({
			comandos: item.category === 'comandos',
			operadores: item.category === 'operadores', 
			valores: item.category === 'valores',
			funcoes: item.category === 'funcoes',
			variaveis: item.category === 'variaveis'
		}));
		
		// Resetar categorias ativas para mostrar a primeira aba
		if (item.category === 'comandos') {
			setActiveCommandCategory(0);
		} else if (item.category === 'operadores') {
			setActiveOperatorCategory(0);
		}
	};

	// Renderizar design RF-TEST (novo) colapsado
	if (isRfTest && sidebarCollapsed) {
		return (
			<div className="sidebar-collapsed">
				<div className="collapsed-content">
					<button 
						className="sidebar-toggle-btn collapsed"
						onClick={toggleSidebar}
						title="Expandir painel de comandos"
					>
						<FontAwesomeIcon icon={faChevronRight} />
					</button>
					
					<div className="quick-access-icons">
						{quickAccessItems.map((item, index) => (
							<div
								key={index}
								className={`quick-icon ${item.category}`}
								onClick={() => handleQuickAccess(item)}
								data-tooltip={item.tooltip}
							>
								<FontAwesomeIcon icon={item.icon} />
							</div>
						))}
					</div>
				</div>
			</div>
		);
	}

	// Renderizar design RF-TEST (novo) expandido
	if (isRfTest) {
		return (
			<div className="sidebar-genia modern">
				<div className="sidebar-header">
					<button 
						className="sidebar-toggle-btn"
						onClick={toggleSidebar}
						title="Fechar painel de comandos"
					>
						<FontAwesomeIcon icon={faChevronLeft} />
					</button>
					<h2 className="sidebar-title">Comandos</h2>
				</div>
				<div className="sidebar-content">
					{/* Comandos */}
					<div className="section-container">
						<div 
							className={`section-header ${expandedSections.comandos ? 'expanded' : ''}`}
							onClick={() => toggleSection('comandos')}
						>
							<h3 className="section-title comandos-title">
								<FontAwesomeIcon icon="fa-solid fa-terminal" />
								Comandos
							</h3>
							<FontAwesomeIcon icon={`fa-solid fa-chevron-${expandedSections.comandos ? 'down' : 'right'}`} className="chevron-icon" />
						</div>
						
						{expandedSections.comandos && (
							<div className="section-content">
								<div className="category-tabs">
									{categories.map((category, index) => {
										const hasItems = devMode || sectionHasImplementedNodes(category.items, implementedNodes);
										if (!hasItems) return null;
										return (
											<button
												key={index}
												className={`category-tab comandos-tab ${activeCommandCategory === index ? "active" : ""}`}
												onClick={() => handleCommandClick(index)}
											>
												{category.title}
											</button>
										);
									})}
								</div>
								{categories[activeCommandCategory] && (devMode || sectionHasImplementedNodes(categories[activeCommandCategory].items, implementedNodes)) && (
									<div className="blocks-grid">
										{(devMode ? categories[activeCommandCategory].items : getImplementedItems(categories[activeCommandCategory].items, implementedNodes)).map((item, index) => (
											<div key={index} className="block-item">
												<NodeDrag 
													icon={item.icon} 
													title={item.title} 
													nodeType={item.nodeType} 
													size="1.2x" 
													category="comandos"
													style={{ cursor: 'grab' }} 
												/>
											</div>
										))}
									</div>
								)}
							</div>
						)}
					</div>

					{/* Operadores */}
					<div className="section-container">
						<div 
							className={`section-header ${expandedSections.operadores ? 'expanded' : ''}`}
							onClick={() => toggleSection('operadores')}
						>
							<h3 className="section-title operadores-title">
								<FontAwesomeIcon icon="fa-solid fa-calculator" />
								Operadores
							</h3>
							<FontAwesomeIcon icon={`fa-solid fa-chevron-${expandedSections.operadores ? 'down' : 'right'}`} className="chevron-icon" />
						</div>
						
						{expandedSections.operadores && (
							<div className="section-content">
								<div className="category-tabs">
									{operatorCategories.map((category, index) => {
										const hasItems = devMode || sectionHasImplementedNodes(category.items, implementedNodes);
										if (!hasItems) return null;
										return (
											<button
												key={index}
												className={`category-tab operadores-tab ${activeOperatorCategory === index ? "active" : ""}`}
												onClick={() => handleOperatorClickNew(index)}
											>
												{category.title}
											</button>
										);
									})}
								</div>
								{operatorCategories[activeOperatorCategory] && (devMode || sectionHasImplementedNodes(operatorCategories[activeOperatorCategory].items, implementedNodes)) && (
									<div className="blocks-grid">
										{(devMode ? operatorCategories[activeOperatorCategory].items : getImplementedItems(operatorCategories[activeOperatorCategory].items, implementedNodes)).map((item, index) => (
											<div key={index} className="block-item">
												<NodeDrag 
													icon={item.icon} 
													title={item.title} 
													nodeType={item.nodeType} 
													size="1.2x" 
													category="operadores"
													style={{ cursor: 'grab' }} 
												/>
											</div>
										))}
									</div>
								)}
							</div>
						)}
					</div>

					{/* Valores */}
					{(devMode || sectionHasImplementedNodes(valoresItems, implementedNodes)) && (
						<div className="section-container">
							<div 
								className={`section-header ${expandedSections.valores ? 'expanded' : ''}`}
								onClick={() => toggleSection('valores')}
							>
								<h3 className="section-title valores-title">
									<FontAwesomeIcon icon="fa-solid fa-cube" />
									Valores
								</h3>
								<FontAwesomeIcon icon={`fa-solid fa-chevron-${expandedSections.valores ? 'down' : 'right'}`} className="chevron-icon" />
							</div>
							
							{expandedSections.valores && (
								<div className="section-content">
									<div className="blocks-grid">
										{(devMode ? valoresItems : getImplementedItems(valoresItems, implementedNodes)).map((item, index) => (
											<div key={index} className="block-item">
												<NodeDrag 
													icon={item.icon} 
													title={item.title} 
													nodeType={item.nodeType} 
													size="1.2x" 
													category="valores"
													style={{ cursor: 'grab' }} 
												/>
											</div>
										))}
									</div>
								</div>
							)}
						</div>
					)}

					{/* Funções */}
					{(devMode || sectionHasImplementedNodes(funcoesItems, implementedNodes)) && (
						<div className="section-container">
							<div 
								className={`section-header ${expandedSections.funcoes ? 'expanded' : ''}`}
								onClick={() => toggleSection('funcoes')}
							>
								<h3 className="section-title funcoes-title">
									<FontAwesomeIcon icon="fa-solid fa-percentage" />
									Funções
								</h3>
								<FontAwesomeIcon icon={`fa-solid fa-chevron-${expandedSections.funcoes ? 'down' : 'right'}`} className="chevron-icon" />
							</div>
							
							{expandedSections.funcoes && (
								<div className="section-content">
									<div className="blocks-grid">
										{(devMode ? funcoesItems : getImplementedItems(funcoesItems, implementedNodes)).map((item, index) => (
											<div key={index} className="block-item">
												<NodeDrag 
													icon={item.icon} 
													title={item.title} 
													nodeType={item.nodeType} 
													size="1.2x" 
													category="funcoes"
													style={{ cursor: 'grab' }} 
												/>
											</div>
										))}
									</div>
								</div>
							)}
						</div>
					)}

					{/* Variáveis */}
					<div className="section-container">
						<div 
							className={`section-header ${expandedSections.variaveis ? 'expanded' : ''}`}
							onClick={() => toggleSection('variaveis')}
						>
							<h3 className="section-title variaveis-title">
								<FontAwesomeIcon icon="fa-solid fa-tags" />
								Variáveis
							</h3>
							<FontAwesomeIcon icon={`fa-solid fa-chevron-${expandedSections.variaveis ? 'down' : 'right'}`} className="chevron-icon" />
						</div>
						
						{expandedSections.variaveis && (
							<div className="section-content">
								<button className="add-variable-btn" onClick={handleAddVariable}>
									<FontAwesomeIcon icon="fa-solid fa-plus" />
									Nova Variável
								</button>
								
								<div className="variables-list">
									{variaveis.map((variavel, index) => (
										<div key={index} className={`variable-card ${!variavel.nome ? 'incomplete' : ''}`}>
											{!variavel.nome && (
												<div className="warning-badge">
													<FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
													Nome obrigatório
												</div>
											)}
											
											<div className="variable-form">
												<div className="form-row">
													<div className="input-field">
														<label>Nome</label>
														<input
															type="text"
															value={variavel.nome}
															onChange={(e) => handleChange(index, "nome", e.target.value)}
															placeholder="nome_variavel"
															className="clean-input"
														/>
													</div>
													<div className="input-field">
														<label>Valor</label>
														<input
															type="text"
															value={variavel.valor}
															onChange={(e) => handleChange(index, "valor", e.target.value)}
															placeholder="0"
															className="clean-input"
														/>
													</div>
												</div>
												
												<div className="variable-actions">
													<button 
														className="delete-btn"
														onClick={() => handleRemoveVariable(index)}
														title="Remover variável"
													>
														<FontAwesomeIcon icon="fa-solid fa-trash" />
													</button>
													
													{variavel.nome && (
														<div
															className={`drag-handle ${draggingIndex === index ? 'dragging' : ''}`}
															draggable
															onDragStart={(e) => {
																setDraggingIndex(index);
																e.dataTransfer.setData('application/variavel', JSON.stringify(variavel));
																e.dataTransfer.effectAllowed = 'move';
															}}
															onDragEnd={() => setDraggingIndex(null)}
															title="Arrastar para o fluxograma"
														>
															<FontAwesomeIcon icon="fa-solid fa-grip-vertical" />
														</div>
													)}
												</div>
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}

	// Renderizar design RF (original)
	return (
		<div className="sidebar-genia classic">
			{/* Comandos */}
			<div className="comandos">
				<h3 className="titulo-sidebar">Comandos</h3>
				<ul className="nav-bar">
					{categories.map((category, index) => {
						const hasItems = devMode || sectionHasImplementedNodes(category.items, implementedNodes);
						return hasItems ? (
							<li
								key={index}
								className={`opcoes ${activeIndex === index ? "active" : ""}`}
								onClick={() => handleClick(index)}
							>
								{category.title}
							</li>
						) : null;
					})}
				</ul>
				<ul className="options">
					{(devMode ? categories[activeIndex].items : getImplementedItems(categories[activeIndex].items, implementedNodes)).map((item, index) => (
						<li key={index} className="opcoes-dnd">
							<NodeDrag icon={item.icon} title={item.title} nodeType={item.nodeType} size="2x" style={{ cursor: 'grab' }} />
						</li>
					))}
				</ul>
			</div>

			{/* Operadores */}
			<div className="operadores">
				<h3 className="titulo-operadores">Operadores</h3>
				<ul className="nav-bar">
					{operatorCategories.map((category, index) => {
						const hasItems = devMode || sectionHasImplementedNodes(category.items, implementedNodes);
						return hasItems ? (
							<li
								key={index}
								className={`opcoes ${activeOperatorIndex === index ? "active" : ""}`}
								onClick={() => handleOperatorClick(index)}
							>
								{category.title}
							</li>
						) : null;
					})}
				</ul>
				<ul className="options">
					{(devMode ? operatorCategories[activeOperatorIndex].items : getImplementedItems(operatorCategories[activeOperatorIndex].items, implementedNodes)).map((item, index) => (
						<li key={index} className="opcoes-dnd">
							<NodeDrag icon={item.icon} title={item.title} nodeType={item.nodeType} size="2x" style={{ cursor: 'grab' }} />
						</li>
					))}
				</ul>
			</div>

			{/* Valores */}
			{(devMode || sectionHasImplementedNodes(valoresItemsClassic, implementedNodes)) && (
				<div className="valores">
					<h3 className="titulo-valores">Valores</h3>
					<ul className="options">
						{(devMode ? valoresItemsClassic : getImplementedItems(valoresItemsClassic, implementedNodes)).map((item, index) => (
							<li key={index} className="opcoes-dnd">
								<NodeDrag 
									icon={item.icon} 
									title={item.title} 
									nodeType={item.nodeType}
									size="2x" 
									style={{ cursor: 'grab' }} 
								/>
							</li>
						))}
					</ul>
				</div>
			)}

			{/* Funções */}
			{(devMode || sectionHasImplementedNodes(funcoesItemsClassic, implementedNodes)) && (
				<div className="funcoes">
					<h3 className="titulo-funcoes">Funções</h3>
					<ul className="options">
						{(devMode ? funcoesItemsClassic : getImplementedItems(funcoesItemsClassic, implementedNodes)).map((item, index) => (
							<li key={index} className="opcoes-dnd">
								<NodeDrag 
									icon={item.icon} 
									title={item.title} 
									nodeType={item.nodeType}
									size="2x" 
									style={{ cursor: 'grab' }} 
								/>
							</li>
						))}
					</ul>
				</div>
			)}

			{/* Variáveis */}
			<div>
				<h3 className="titulo-variaveis">Variáveis</h3>
				<div className="variaveis">
					<button className="variaveis-button" onClick={handleAddVariable}>
						Criar variável
					</button>
					<div className="variaveis-container">
						{variaveis.map((variavel, index) => (
							<div key={index} style={{ display: "flex", flexDirection: "column", alignItems: "center", backgroundColor: !variavel.nome ? "#f0f0f0" : "", marginBottom: 4, borderRadius: 4 }}>
								{/* Aviso se não tem nome */}
								{!variavel.nome && (
									<div style={{ color: 'red', fontWeight: 'bold', marginBottom: 2, fontSize: 12 }}>
										DEFINA O NOME DA VARIAVEL PARA USO
									</div>
								)}
								<div className="variavel-item">
									<div className="variavel-info">
										<label>Variável:</label>
										<input
											type="text"
											className="variavel-input"
											value={variavel.nome}
											onChange={(e) => handleChange(index, "nome", e.target.value)}
											placeholder="Nome"
										/>
										<CancelPresentationIcon />
									</div>
									<div className="variavel-valor">
										<label>Valor:</label>
										<input
											type="text"
											className="variavel-input"
											value={variavel.valor}
											onChange={(e) => handleChange(index, "valor", e.target.value)}
											placeholder="Valor"
										/>
									</div>
									<DeleteForeverIcon
										onClick={() => handleRemoveVariable(index)}
										style={{ cursor: "pointer", color: "red" }}
									/>
									{/* Ícone de arrastar só aparece se tem nome */}
									{variavel.nome && (
										<div
											className="dnd-option"
											draggable
											onDragStart={(e) => {
												setDraggingIndex(index);
												e.dataTransfer.setData('application/variavel', JSON.stringify(variavel));
												e.dataTransfer.effectAllowed = 'move';
											}}
											onDragEnd={() => setDraggingIndex(null)}
											onMouseDown={() => setDraggingIndex(index)}
											onMouseUp={() => setDraggingIndex(null)}
											onMouseLeave={() => setDraggingIndex(null)}
											style={{ marginLeft: 8, cursor: draggingIndex === index ? 'grabbing' : 'grab' }}
										>
											<FontAwesomeIcon icon={faListOl} className="icone" size="2x" />
										</div>
									)}
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};